<template>
  <v-text-field
      rounded="lg"
      :error-messages="error"
      :label="translation(translationList.number_of_days, 'Număr zile')"
      v-model="numberOfDaysString"
      name="number_of_days"
      :rules="numberOfDaysValidationRules"
      @input="onInput"
      variant="outlined"
      bg-color="surface"
      :tile="true"
  >
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: Number as () => number | null,
  },
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const numberOfDays = ref<number | null>(modelValue.value);
    const numberOfDaysString = ref<string | null>(modelValue.value !== null ? String(modelValue.value) : null);
    const error = ref<string[]>([]);

    watch(numberOfDays, (newValue) => {
      const isValid = validateNumberOfDays(newValue);
      if (isValid) {
        emit('update:modelValue', newValue);
      } else {
        emit('update:modelValue', null);
      }
      emit('validation-travel-status', { field: 'number_of_days', isValid });
    });

    watch(numberOfDaysString, (newValue) => {
      const parsedValue = parseFloat(newValue as string);
      if (!isNaN(parsedValue)) {
        numberOfDays.value = parsedValue;
      } else {
        numberOfDays.value = null;
      }
    });

    function validateNumberOfDays(value: number | null, showErrors?: boolean): boolean {
      error.value = [];
      if (value !== null) {
        if (showErrors) {
          const errors = numberOfDaysValidationRules
              .map(rule => rule(value))
              .filter(result => result !== true) as string[];
          if (errors.length > 0) {
            error.value = errors;
            return false;
          }
        }
        return numberOfDaysValidationRules.every(rule => rule(value) === true);
      } else if (showErrors) {
        error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        return false;
      }
      return false;
    }

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const numberOfDaysValidationRules = [
      (v: number) => !!v || translation(translationList.value.required, 'Acest camp este obligatoriu'),
      (v: number) => v >= 1 || translation(translationList.value.invalid_number_of_days, 'Durata trebuie să fie de cel puțin 1 zi'),
    ];

    const onInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      numberOfDaysString.value = input.value;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      // Listen for validate event
      window.addEventListener('validate-travel', (data) => {
        if (data.detail && data.detail === 'number_of_days') {
          const isValid = validateNumberOfDays(numberOfDays.value, true);
          if (isValid) {
            emit('validation-travel-status', { field: 'number_of_days', isValid });
          }
        }
      });
    });

    watch(modelValue, (newValue) => {
      const formattedValue = newValue !== null ? String(newValue) : null;
      if (formattedValue !== numberOfDaysString.value) {
        numberOfDaysString.value = formattedValue;
      }
    });

    return {
      numberOfDays,
      numberOfDaysString,
      numberOfDaysValidationRules,
      translationList,
      translation,
      onInput,
      error,
    };
  },
});
</script>

<style scoped>
</style>